import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending requests (VINEY: with the controller that can drop them)
   * @type {Map<string, { promise: Promise<string>, controller: AbortController }>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML, { mode });
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {{ deadline?: number }} [options] - VINEY: deadline in ms after which the request gives up
   *   (only the rule 8 waits pass one: filters and load more; header hydration and the cart do not)
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), options = {}) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    // VINEY (T679 rule 8, session 2): one shared request per address, validated inside the shared
    // promise, so every caller gets the same answer: an error page (by status, or a 200 without the
    // section asked for) is a failure for all of them and is never cached or kept (Horizon left a
    // rejected promise in #pendingPromises, so every retry of the same URL got the old failure back).
    let entry = this.#pendingPromises.get(sectionUrl);
    if (!entry) {
      if (useCache) {
        const cachedHTML = this.#cache.get(sectionUrl);

        if (cachedHTML) return cachedHTML;
      }

      const controller = new AbortController();
      const wanted = `id="${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}"`;
      const promise = fetch(sectionUrl, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        })
        .then((sectionHTML) => {
          if (!sectionHTML.includes(wanted)) throw new Error(`Section ${sectionId} missing from the answer`);
          this.#cache.set(sectionUrl, sectionHTML);
          return sectionHTML;
        })
        .finally(() => {
          if (this.#pendingPromises.get(sectionUrl) === entry) this.#pendingPromises.delete(sectionUrl);
        });
      // a request nobody awaits (a hover prefetch) must not report an unhandled failure
      promise.catch(() => {});
      entry = { promise, controller };
      this.#pendingPromises.set(sectionUrl, entry);
    }

    if (!options.deadline) return entry.promise;

    // VINEY: each caller's own limit, also when it joins a request another caller started (a hover
    // prefetch has none); at the limit the shared request is dropped, so "Reyna aftur" asks afresh
    const shared = entry;
    let timer = 0;
    const limit = new Promise((_, reject) => {
      timer = setTimeout(() => {
        if (this.#pendingPromises.get(sectionUrl) === shared) {
          this.#pendingPromises.delete(sectionUrl);
          shared.controller.abort();
        }
        reject(new Error(`Section ${sectionId} gave no answer in time`));
      }, options.deadline);
    });
    try {
      return await Promise.race([shared.promise, limit]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * VINEY (T679 rule 8): the cached answer for an address, if there is one (no request).
   * @param {string} sectionId - The section ID
   * @param {URL} url - The URL the section was rendered for
   * @returns {string | undefined}
   */
  peekSectionHTML(sectionId, url) {
    return this.#cache.get(buildSectionRenderingURL(sectionId, new URL(url)));
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/** VINEY (T679 rule 8): filters and load more give up on a request that has not answered in 10 s. */
export const SECTION_DEADLINE_MS = 10000;

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {Document} fragment - The parsed response document
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
